import {
  GET_ADMIN_USER_NOTIFICATIONS,
  CREATE_USER_NOTIFICATIONS,
  UPDATE_USER_NOTIFICATIONS,
  DELETE_USER_NOTIFICATIONS,
  EXPORT_ADMIN_USER_NOTIFICATIONS,
  BULK_EDIT_USER_NOTIFICATIONS,
  GET_USER_NOTIFICATIONS_BULK_EDIT_STATUS,
  PREVIEW_USER_NOTIFICATIONS_BULK_EDIT,
} from "./actionTypes"
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects"
import { getAdminUserNotificationsSucess, getAdminUserNotificationsError, createUserNotificationsError, createUserNotificationsSucess, updateUserNotificationsSucess, updateUserNotificationsError, deleteUserNotificationsSucess, deleteUserNotificationsError, exportAdminUserNotificationsSuccess, exportAdminUserNotificationsError,
  bulkEditUserNotificationsSuccess,
  bulkEditUserNotificationsError,
  getUserNotificationsBulkEditStatusSuccess,
  getUserNotificationsBulkEditStatusError,
  previewUserNotificationsBulkEditSuccess,
  previewUserNotificationsBulkEditError,
} from "./actions"
import { AppConfiguration } from "config";
import { sf_fetch } from "helpers/sf_fetch";
import { responseErrorChecker } from "helpers/sf_utils";
import { genericDeleteRequest, genericGetRequest_2, genericPostRequest, genericPutRequest } from "helpers/sf_backend_helper";
import { bsh_getAdminUserDetails } from "helpers/sf_browser_storage_helper";
import ToastAlert, { ToastAlertIconType } from "components/Common/ToastAlert/ToastAlert";
import { buildCampaignAnalysisCacheKey as buildPaginationCacheKey } from "helpers/sf_utils"
import { showExportEnqueuedToast } from "helpers/export_toast_helper"

const inFlightNotificationRequests = new Set()

function* onGetAdminUserNotifications(action) {
  const useCache = Boolean(action.payload?.useCache)
  const queryParams = action.payload?.queryParams || {}
  const requestedPage = parseInt(queryParams.page_number, 10) || 1
  const cacheKey = useCache ? buildPaginationCacheKey(queryParams) : null
  const requestKey = `${cacheKey}:${requestedPage}`

  if (useCache) {
    const cachedPage = yield select(
      (state) => state.NotificationsTerminal.page_cache?.[cacheKey]?.[requestedPage]
    )
    if (cachedPage) return
    if (inFlightNotificationRequests.has(requestKey)) return
    inFlightNotificationRequests.add(requestKey)
  }

  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_user_notifications`,
      queryParams,
      {
        "Authorization": user.token,
        "Content-type": "application/json; charset=UTF-8",
      }
    )
    yield put(getAdminUserNotificationsSucess(response, { queryParams, useCache }))
  } catch (error) {
    yield put(getAdminUserNotificationsError(error))
  } finally {
    if (useCache) {
      inFlightNotificationRequests.delete(requestKey)
    }
  }
}

function* onCreateNotificationsTerminal(action) {
  try {
    let user = bsh_getAdminUserDetails();

    const response = yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/user_notifications`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          "Authorization": user.token,
          "Content-type": "application/json; charset=UTF-8"
        }
      }
    )
    yield put(createUserNotificationsSucess(response))
  } catch (error) {
    yield put(createUserNotificationsError(error))
  }
}

function* onEditNotificationsTerminal(action) {
  try {
    let user = bsh_getAdminUserDetails();

    const response = yield call(
      genericPutRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/user_notifications/${action.payload.id}`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          "Authorization": user.token,
          "Content-type": "application/json; charset=UTF-8"
        }
      }
    )
    yield put(updateUserNotificationsSucess(response))
  } catch (error) {
    yield put(updateUserNotificationsError(error))
  }
}

function* onDeleteUserNotifications(action) {
  try {
    let user = bsh_getAdminUserDetails();
    const response = yield call(
      genericDeleteRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_user_notifications/${action.payload.id}`,
      {
        headers: {
          "Authorization": user.token,
          "Content-type": "application/json; charset=UTF-8"
        }
      }
    )
    yield put(deleteUserNotificationsSucess(response))
  } catch (error) {
    yield put(deleteUserNotificationsError(error))
  }
}


function* onExportAdminUserNotifications(action) {
  try {
    let user = bsh_getAdminUserDetails();
    yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_user_notifications/export`,
      {
        body: JSON.stringify({ ...(action.payload?.queryParams || {}) }),
        headers: {
          "Authorization": user.token,
          "Content-type": "application/json; charset=UTF-8"
        }
      }
    )
    yield put(exportAdminUserNotificationsSuccess())
    showExportEnqueuedToast({ reportExportsAvailable: true })
  } catch (error) {
    yield put(exportAdminUserNotificationsError(error))
    ToastAlert.fire({
      icon: ToastAlertIconType.ERROR,
      title: 'Failed to queue notifications export.',
    })
  }
}

// Notifications Terminal bulk edits run through the shared, async bulk edit
// flow (same as the CPA Optimizer), via its admin-token endpoints.
const ADMIN_BULK_EDITS_URL = `${AppConfiguration.BACKEND_URL}/api/v1/admin_bulk_edits`
const NOTIFICATIONS_TERMINAL_BULK_EDIT_TARGET_KEY = "notifications_terminal"
const ACTIVE_BULK_EDIT_STATUSES = ["queued", "in_progress", "cancellation_requested", "processing"]

const normalizeBulkEditJob = job => {
  if (!job) return null

  // cancellation_requested still holds the lock until the worker
  // acknowledges it, so the page must stay locked for it too.
  const status = ["queued", "in_progress", "cancellation_requested"].includes(job.status)
    ? "processing"
    : job.status
  return { ...job, status }
}

// select_all targets every notification the list shows (all pages);
// otherwise only the explicitly picked ids.
const buildBulkEditPayload = payload => ({
  target_key: NOTIFICATIONS_TERMINAL_BULK_EDIT_TARGET_KEY,
  selection: payload?.select_all
    ? { mode: "filters", filters: payload?.filters || {} }
    : { mode: "ids", ids: Array.isArray(payload?.ids) ? payload.ids : [] },
  changes: payload?.changes || {},
})

const adminAuthHeaders = () => ({ Authorization: bsh_getAdminUserDetails().token })

// preview renders its payload at the top level rather than under `data`,
// so genericPostRequest's `.data` unwrap can't be used for it.
const postRequestRaw = async (url, init) => {
  const response = await sf_fetch(url, { method: "POST", ...init })
  const responseText = await responseErrorChecker(response)
  return JSON.parse(responseText)
}

function* onBulkEditUserNotifications(action) {
  try {
    const payload = buildBulkEditPayload(action.payload)
    if (payload.selection.mode === "ids" && payload.selection.ids.length === 0) {
      yield put(bulkEditUserNotificationsError(new Error("No notifications selected for bulk edit")))
      return
    }
    const response = yield call(genericPostRequest, ADMIN_BULK_EDITS_URL, {
      body: JSON.stringify(payload),
      headers: { ...adminAuthHeaders(), "Content-Type": "application/json" },
    })
    yield put(bulkEditUserNotificationsSuccess(normalizeBulkEditJob(response)))
  } catch (error) {
    yield put(bulkEditUserNotificationsError(error))
  }
}

function* onPreviewUserNotificationsBulkEdit(action) {
  try {
    const payload = buildBulkEditPayload(action.payload)
    if (payload.selection.mode === "ids" && payload.selection.ids.length === 0) {
      yield put(previewUserNotificationsBulkEditError(new Error("No notifications selected for bulk edit")))
      return
    }
    const response = yield call(postRequestRaw, `${ADMIN_BULK_EDITS_URL}/preview`, {
      body: JSON.stringify(payload),
      headers: { ...adminAuthHeaders(), "Content-Type": "application/json" },
    })
    yield put(previewUserNotificationsBulkEditSuccess(response))
  } catch (error) {
    yield put(previewUserNotificationsBulkEditError(error))
  }
}

// Follows the job this page started, or otherwise asks whether another
// admin's Notifications Terminal bulk edit currently holds the lock.
function* onGetUserNotificationsBulkEditStatus() {
  try {
    const headers = adminAuthHeaders()
    const currentJob = yield select(state => state?.NotificationsTerminal?.bulk_edit_job)
    let response = null

    if (currentJob?.id && ACTIVE_BULK_EDIT_STATUSES.includes(currentJob.status)) {
      response = yield call(genericGetRequest_2, `${ADMIN_BULK_EDITS_URL}/${currentJob.id}`, {}, headers)
    } else {
      const lockStatus = yield call(
        genericGetRequest_2,
        `${ADMIN_BULK_EDITS_URL}/lock_status`,
        { target_key: NOTIFICATIONS_TERMINAL_BULK_EDIT_TARGET_KEY },
        headers
      )
      if (lockStatus?.active_request_id) {
        try {
          response = yield call(genericGetRequest_2, `${ADMIN_BULK_EDITS_URL}/${lockStatus.active_request_id}`, {}, headers)
        } catch (_error) {
          // Still locked even if the job details can't be read; stay locked.
          response = { id: lockStatus.active_request_id, status: lockStatus.status || "in_progress" }
        }
      }
    }

    yield put(getUserNotificationsBulkEditStatusSuccess(normalizeBulkEditJob(response)))
  } catch (error) {
    yield put(getUserNotificationsBulkEditStatusError(error))
  }
}

function* NotificationsTerminalSaga() {
  yield takeEvery(GET_ADMIN_USER_NOTIFICATIONS, onGetAdminUserNotifications);
  yield takeEvery(CREATE_USER_NOTIFICATIONS, onCreateNotificationsTerminal);
  yield takeEvery(UPDATE_USER_NOTIFICATIONS, onEditNotificationsTerminal);
  yield takeEvery(DELETE_USER_NOTIFICATIONS, onDeleteUserNotifications);
  yield takeEvery(EXPORT_ADMIN_USER_NOTIFICATIONS, onExportAdminUserNotifications);
  yield takeEvery(BULK_EDIT_USER_NOTIFICATIONS, onBulkEditUserNotifications);
  yield takeLatest(GET_USER_NOTIFICATIONS_BULK_EDIT_STATUS, onGetUserNotificationsBulkEditStatus);
  yield takeLatest(PREVIEW_USER_NOTIFICATIONS_BULK_EDIT, onPreviewUserNotificationsBulkEdit);
}

export default NotificationsTerminalSaga
